import { useCallback, useEffect, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Step from "@mui/material/Step";
import StepButton from "@mui/material/StepButton";
import Stepper from "@mui/material/Stepper";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Link } from "react-router-dom";
import { AGREEMENT_LABEL, type AgreementKind } from "@shared/content-agreement";
import { foyerApi, ownerIngestApi, type IngestJobView } from "../../http/foyer";
import {
  isContainerType,
  isVodAssetType,
  PARENT_FIELDS_BY_TYPE,
  type VodAssetType,
} from "@shared/vod-title";
import { uploadInBlocks } from "../../lib/blockUpload";
import {
  measureVideoDuration,
  minutesToSeconds,
  secondsToMinutes,
} from "../../lib/videoDuration";
import {
  VOD_WIZARD_STEPS,
  VOD_WIZARD_LABELS,
  type VodWizardStep,
  type VodWizardStepResult,
} from "@shared/vod-wizard";
import {
  emptyAssetDraft,
  draftToUpdate,
  emptiedCollections,
  assetToDraft,
  type AssetDraft,
} from "@shared/asset-draft";
import type { VodTitleRow } from "../../http/foyer";
import type { Genre } from "@shared/types/content-api";
import { Field, SectionHead, StringListField } from "./editorBits";
import AvailabilityEditor from "./AvailabilityEditor";
import LocalisedContentPanel from "./LocalisedContentPanel";
import LocalisedImagesPanel from "./LocalisedImagesPanel";
import SubtitlesTab from "./SubtitlesTab";

/**
 * Creating a VOD title, one step at a time.
 *
 * ## Why this replaces a single form
 *
 * `AssetEditorDialog` asks for everything at once — around ninety controls for
 * a realistic title — and then ingest happened somewhere else entirely, fired
 * by a FILE PICKER on an already-saved asset. Choosing a file was the same
 * gesture as committing a shared serial transcoder and a delivery to an
 * external partner, and nothing in between said so.
 *
 * So the order is the point, not the decoration: identity, then the file, then
 * the subtitles that CANNOT be added after delivery, then the things a reviewer
 * reads, and only then a deliberate hand-in.
 *
 * ## 🚨 The ticks come from the server, always
 *
 * Every tick on the rail is `GET …/wizard`, which the same route that gates the
 * submission computes from the same `wizardSteps()`. The dialog holds no
 * opinion about whether a step is done.
 *
 * That is deliberate and it costs a round trip per change. The alternative —
 * deriving ticks locally from the form state — drifts the moment the two
 * disagree, and the way it shows is a green rail followed by a refusal the
 * operator cannot account for. `SupplierTitlePage` already states the principle
 * for this area: *"a wizard step is a suggestion"*; the server decides.
 *
 * ## What the dialog owns, and what it does not
 *
 * It owns the ORDER and the local draft between saves. It owns no rules: the
 * gate, the completeness ladder and every refusal live on the server, and this
 * only ever draws them.
 *
 * ## 🚩 ONE component, TWO surfaces — the same pattern as the routes
 *
 * `vodTitles.ts` and `ingestJobs.ts` each register one set of handlers twice,
 * under `/distribution` and `/owner`, for a reason stated there: *a refusal
 * added to the staff PATCH and forgotten on the owner PATCH does not break a
 * test, it opens a hole.* The client half of that discipline is `ingestCalls`,
 * which binds the base once so a supplier call cannot be written against a
 * staff URL.
 *
 * This dialog follows it. A second supplier wizard would drift within a
 * release, and the drift would be silent — a step quietly relaxed on one side.
 * Exactly three things differ, and all three come from the SERVER:
 *
 *  1. which base the calls go to (`bind`),
 *  2. whether `packages` is a step at all (the ladder says `skipped`),
 *  3. what the last button promises (the gate's verdict, not a guess here).
 */

/** The rail: the shared ladder, plus a hand-in that is not a "step" of data. */
const RAIL: Array<{ key: VodWizardStep | "review"; label: string }> = [
  ...VOD_WIZARD_STEPS.map((step) => ({
    key: step,
    label: VOD_WIZARD_LABELS[step],
  })),
  { key: "review" as const, label: "review & send" },
];

/**
 * The kinds this wizard will create.
 *
 * ## 🚨 Movies only, on purpose, and it is one line to change back
 *
 * The full picker — all four types, a parent dropdown per allowed link, seasons
 * filtered to the chosen series — is built and tested in `e0af9aa` and
 * `204791b`. It is switched off here rather than deleted, because the reason it
 * came off is a product decision (Sigge, 2026-08-28) and not a fault in it.
 *
 * ⚠️ It was also WRONG on the data in one way worth remembering before anyone
 * turns it on again: measured against the live catalogue, **no series carries
 * `contentRights`** (0 of 83), and `wizardSteps()` demands `rightsCount > 0`
 * from every type. So a series created here could never be handed in — it would
 * sit on the rights step forever. A container skips master and subtitles; it
 * does NOT skip rights, and making it do so is a separate decision about what a
 * licence window on a series would even mean.
 *
 * Everything else stands down on its own, because it is already keyed on the
 * chosen kind: `PARENT_FIELDS_BY_TYPE.movie` is `[]`, so no parent field
 * renders, and the two lookup effects return early for anything that is not a
 * season or an episode. Add a type back to this array and the whole path
 * returns with it.
 *
 * What the model really supports, if that day comes:
 *   - `seriesId` and `seasonId` on an episode are the grouping keys, and BOTH
 *     are populated on 100% of real episodes.
 *   - `seasonNumber` on an episode is an override that falls back to the season
 *     row's own number — populated on 2 of 600 real episodes, both `0`. It is a
 *     repair field, not something to ask a supplier for.
 *   - Nothing is required upstream: there is no foreign key at all, so a wrong
 *     id files an episode inside somebody else's series rather than failing.
 */
const WIZARD_KINDS: readonly VodAssetType[] = ["movie"];

/** Plain words for the four parent links, so a supplier is not shown a key. */
const PARENT_FIELD_LABELS: Record<string, string> = {
  seriesId: "Series id",
  seasonId: "Season id",
  seasonNumber: "Season number",
  episodeNumber: "Episode number",
};

const TITLE_CASE = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1);

/**
 * A title from a filename (`ART-9`).
 *
 * The bulk path already offered this courtesy — *"we will take its name from
 * the filename"* — and losing it when the single-file picker moved to the
 * wizard would make the guided path feel like more work than the one it
 * replaced. Only the extension goes: separators are left alone, because
 * turning `Den.skyldige.2018.mp4` into words is guesswork, and a name the
 * operator can see and correct beats one that is silently wrong.
 */
export function titleFromFilename(name: string): string {
  return name.replace(/\.[^./\\]+$/, "").trim();
}

export default function VodWizardDialog({
  open,
  onClose,
  onFinished,
  surface = "staff",
  initialFile = null,
}: {
  open: boolean;
  onClose: () => void;
  /** A title was created, so the list behind the dialog is stale. */
  onFinished: () => void;
  /**
   * A master the operator has ALREADY chosen, from the Upload page's
   * single-file picker (`ART-9`).
   *
   * Optional, and must stay optional: `New title` opens this same component
   * with nothing in hand. When it is set, the title name is seeded from the
   * filename and the master upload starts on its own once the row exists —
   * the file is not asked for a second time.
   */
  initialFile?: File | null;
  /**
   * Which set of routes to speak to. Bound once, here, so no call in the body
   * can be written against the other surface's URL.
   */
  surface?: "staff" | "owner";
}) {
  const owner = surface === "owner";
  const api = owner ? ownerIngestApi : foyerApi;
  const createTitleCall = owner
    ? foyerApi.createOwnTitle
    : foyerApi.createVodTitle;
  const updateTitleCall = owner
    ? foyerApi.updateOwnTitle
    : foyerApi.updateVodTitle;
  const [index, setIndex] = useState(0);
  const [draft, setDraft] = useState<AssetDraft>(() =>
    emptyAssetDraft("movie")
  );
  const [seed, setSeed] = useState<VodTitleRow | null>(null);
  const [assetId, setAssetId] = useState<string | null>(null);
  const [ladder, setLadder] = useState<VodWizardStepResult[]>([]);
  const [job, setJob] = useState<IngestJobView | null>(null);
  const [packages, setPackages] = useState<Array<{ id: string; name: string }>>(
    []
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [upload, setUpload] = useState<{
    pct: number | null;
    label: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<{ dispatch: boolean } | null>(null);
  const [panel, setPanel] = useState<"text" | "images" | null>(null);
  /** Which documents are outstanding, when the terms gate has refused. */
  const [agreementNeeded, setAgreementNeeded] = useState<string | null>(null);
  const [genres, setGenres] = useState<Genre[]>([]);
  const filePick = useRef<HTMLInputElement | null>(null);
  /**
   * The caller's own series, and the seasons of the one they picked.
   *
   * 🚨 A supplier must not be asked to paste an assetId. Measured against the
   * live catalogue: a SERIES carries no `seriesId` of its own — it is the root,
   * and its seasons and episodes reference the series' OWN assetId. So the id
   * a season needs is never visible on the season's screen; it belongs to a row
   * somewhere else. There is no foreign key upstream either, so a mistyped one
   * does not fail — it files the episode inside somebody else's series.
   *
   * Both lists come from the same handler the staff list uses
   * (`registerTitleRoutes` mounts one body under both bases), so a supplier
   * only ever sees their own rows: `readTitle`'s ownership check is on the
   * route, not on this call.
   */
  const [parents, setParents] = useState<{
    series: { assetId: string; originalTitle: string }[];
    seasons: { assetId: string; originalTitle: string }[];
  }>({ series: [], seasons: [] });

  /**
   * `AssetDraft.type` can also be `channel`, which this wizard never authors —
   * a channel is a live feed with its own page, not something anybody uploads a
   * master for. Narrowed once, here, rather than cast at each of the four
   * places that ask what kind of thing is being made.
   */
  const kind: VodAssetType = isVodAssetType(draft.type) ? draft.type : "movie";

  /**
   * The parent lookup, bound by surface — and the two are NOT the same call.
   *
   * ⚠️ The owner list refuses `type` and every other narrowing with
   * `filter_not_supported_here`: its page is built from Foyer's ownership
   * mapping, which holds ids rather than metadata, so filtering on the wire
   * could not report an honest total. `listOwnTitlesOfType` pages and filters
   * client-side instead. The staff list has the real filters and uses them.
   *
   * Writing one call for both would have 400'd every supplier — which is
   * exactly the class of thing this wizard's header warns about.
   */
  const listParents = useCallback(
    (filter: { type: VodAssetType; seriesId?: string }) =>
      owner
        ? foyerApi.listOwnTitlesOfType(filter)
        : foyerApi.listVodTitles({
            type: [filter.type],
            ...(filter.seriesId ? { seriesId: filter.seriesId } : {}),
            pageSize: 200,
          }),
    [owner]
  );
  const listGenresCall = owner
    ? foyerApi.listOwnGenres
    : foyerApi.listContentGenres;

  /**
   * The genre catalogue, PICKED and never typed.
   *
   * `Genre.name` is unique upstream and `genreId` is the caller's to choose, so
   * an invented genre is a permanent second shelf in a catalogue Foyer does not
   * own. That is why this could not be a text box on the supplier surface — and
   * why `/owner/genres` had to exist before the field could be here at all.
   */
  useEffect(() => {
    if (!assetId) return;
    let live = true;
    void listGenresCall()
      .then((rows) => {
        if (live) setGenres(rows);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [assetId, listGenresCall]);

  // The series list, only while a kind that sits inside one is selected and
  // the row does not exist yet — after create the fields are frozen anyway.
  useEffect(() => {
    if (assetId) return;
    if (kind !== "season" && kind !== "episode") return;
    let live = true;
    void listParents({ type: "series" })
      .then((page) => {
        if (live) setParents((p) => ({ ...p, series: page.titles }));
      })
      // A picker that cannot load is not an error worth stopping the wizard
      // for: the field falls back to nothing selected, and the step's own
      // refusal explains what is missing.
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [assetId, kind, listParents]);

  // The seasons OF the chosen series. Refetched when that choice changes, so
  // an episode can never be offered a season from a different series.
  useEffect(() => {
    if (assetId || kind !== "episode" || !draft.seriesId) {
      setParents((p) => (p.seasons.length ? { ...p, seasons: [] } : p));
      return;
    }
    let live = true;
    void listParents({ type: "season", seriesId: draft.seriesId })
      .then((page) => {
        if (live) setParents((p) => ({ ...p, seasons: page.titles }));
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [assetId, kind, draft.seriesId, listParents]);

  /**
   * Which language an uploaded picture is filed under.
   *
   * Derived, never a control: one more dropdown on this step would be a
   * decision asked before there is anything to decide about. It follows the
   * artwork already there, then the title's own language — and `Manage
   * artwork` is where a second language is added deliberately.
   */
  const step = RAIL[index]?.key ?? "review";
  const stateOf = (key: VodWizardStep) =>
    ladder.find((entry) => entry.step === key);
  const missing = ladder
    .filter((entry) => entry.state === "todo")
    .map((entry) => entry.step);

  /** Re-read the ladder. The ONE source of every tick on the rail. */
  const refresh = useCallback(
    async (id: string) => {
      const answer = await api.vodWizard(id);
      setLadder(answer.steps);
      setJob(answer.job);
    },
    [api]
  );

  useEffect(() => {
    if (!open) return;
    // Reset on every opening: a wizard that remembers the last title is a
    // wizard that silently edits it.
    setIndex(0);
    // A file in hand means the operator already said which film this is, so
    // the name is seeded the way the batch path used to: from the filename.
    // The distributor id is still asked for — nothing can guess that.
    setDraft(
      initialFile
        ? {
            ...emptyAssetDraft("movie"),
            originalTitle: titleFromFilename(initialFile.name),
          }
        : emptyAssetDraft("movie")
    );
    setSeed(null);
    setAssetId(null);
    setLadder([]);
    setJob(null);
    setError(null);
    setSent(null);
    /**
     * ⚠️ Staff only, and not merely hidden. `GET /distribution/entitlements`
     * is refused on the owner surface — a supplier may not even SEE the list of
     * packages, because which bundles exist is commercial information about
     * other partners.
     */
    if (owner) {
      setPackages([]);
      return;
    }
    foyerApi
      .listIngestEntitlements()
      .then((r) => setPackages(r.collections))
      .catch(() => setPackages([]));
  }, [open, owner, initialFile]);

  function fail(e: unknown) {
    const err = e as {
      code?: string;
      message?: string;
      missing?: string[];
      outstanding?: string[];
    };
    /**
     * 🚨 The terms gate, said in words a partner can act on (`CONTENT-19`).
     *
     * `POST …/upload-url` refuses an owner who has not accepted the agreement,
     * and it NAMES the outstanding documents precisely so a client can point at
     * them — the route says so in its own comment. Nothing here read that, so a
     * supplier got the raw refusal on the video step: a wall, with no statement
     * of what was missing and no route to the thing they had to sign.
     *
     * It is the first hard stop in the flow and the only one that is not about
     * the material at all, which makes it the worst one to leave undecoded.
     */
    if (err.code === "agreement_not_accepted") {
      const docs = (err.outstanding ?? [])
        .map((kind) => AGREEMENT_LABEL[kind as AgreementKind] ?? kind)
        .join(", ");
      setAgreementNeeded(docs || "the content agreement");
      setError(null);
      return;
    }
    setError(
      err.code === "submission_incomplete"
        ? `Not sent — still missing: ${(err.missing ?? [])
            .map((s) => VOD_WIZARD_LABELS[s as VodWizardStep] ?? s)
            .join(", ")}.`
        : err.code === "external_id_required"
          ? "Your own reference cannot be blanked once the title exists. Put something back, or leave what is there."
          : err.code === "ingest_already_running"
            ? "An ingest for this title is already running — only one at a time, or two packages would fight over the same folder."
            : err.code === "storage_not_configured"
              ? "Storage is not configured for ingest yet, so there is nowhere to put the file."
              : (err.message ?? "Something went wrong.")
    );
  }

  async function run(label: string, work: () => Promise<void>) {
    setError(null);
    setBusy(label);
    try {
      await work();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  }

  /**
   * Step one commits the title, and nothing before it touches the catalogue.
   *
   * ⚠️ The row is LIVE the instant it is POSTed — the frozen catalogue has no
   * draft flag. It is invisible only because it is in no package yet, which is
   * exactly the state the packages step exists to end deliberately.
   */
  async function createTitle() {
    await run("Creating the title…", async () => {
      const parents = Object.fromEntries(
        PARENT_FIELDS_BY_TYPE[kind]
          .map((field) => [field, draft[field as "seriesId"].trim()])
          .filter(([, value]) => value !== "")
      );
      const row = await createTitleCall({
        type: kind,
        sourceType: "vod",
        externalId: draft.externalId.trim(),
        originalTitle: draft.originalTitle.trim(),
        ...parents,
      });
      setAssetId(row.assetId);
      setSeed(row);
      /**
       * 🚨 Re-seed the DRAFT from the answer, not just the seed.
       *
       * `scalarPayload` sends `externalId` on every PATCH, always — it is not
       * diffed. So after a blank create, where the server minted `foyer-…`
       * (`ART-10`), a draft still holding `""` made the very next save PATCH an
       * empty externalId, and the PATCH refuses that with
       * `external_id_required` because a stated blank means "erase it".
       *
       * The symptom was a 400 on the DESCRIPTION step — three steps and several
       * minutes away from the field that caused it, and about a value the
       * operator never typed. Reported exactly that way.
       *
       * The rule this breaks is already written down for the artwork upload:
       * re-seed from what came back, never carry a local guess about what the
       * row now contains. Here it also means the minted id is visible in the
       * field, which is the honest place for it.
       */
      setDraft(assetToDraft(row));
      await refresh(row.assetId);
      /**
       * The file the operator already chose goes now (`ART-9`), so they are
       * not asked for it twice.
       *
       * ⚠️ Deliberately NOT paired with a jump to step two — see below. The
       * upload indicator sits above the step body precisely so this is visible
       * from step one while the confirmation is still being read.
       */
      if (initialFile) void uploadMaster(initialFile, row.assetId);
      /**
       * ⚠️ Deliberately does NOT jump to step two.
       *
       * Auto-advancing raced the confirmation off the screen — the operator
       * never saw that the row is live and invisible, which is the one
       * surprising thing about step one. The rail is already unlocked; letting
       * them press Next costs a click and buys the sentence being read.
       */
      onFinished();
    });
  }

  /** Save whatever the metadata steps changed, then re-read the ladder. */
  async function saveDraft(next: AssetDraft) {
    setDraft(next);
    if (!assetId) return;
    await run("Saving…", async () => {
      /**
       * ⚠️ `draftToUpdate` needs the asset the draft was SEEDED FROM, because
       * PATCH is destructive-replace on seven collections upstream — it states
       * a collection only when it differs from what was read. Passing a stale
       * seed would silently empty whatever the wizard has not touched yet.
       */
      if (!seed) return;
      /**
       * 🚨 Confirm a collection this wizard actually EMPTIED.
       *
       * `refuseCollectionWipe` refuses a stated `[]` unless the caller says it
       * meant it, and it is right to: a half-loaded form sending an empty array
       * deletes every row upstream. But only `AssetEditorDialog` ever passed
       * the confirmation, so on the two SUPPLIER-facing surfaces removing the
       * last picture answered `collection_would_be_emptied` and there was no
       * control anywhere that could say yes — the wrong-poster dead end
       * `ART-8` named, reached by a different road than the one it guessed.
       *
       * A panel that emptied a list IS the confirmation: it was open, it drew
       * the rows, and somebody pressed Remove on each of them. `emptiedCollections`
       * is asked rather than a blanket `true`, so a collection this wizard has
       * never shown still cannot be wiped by a stale draft.
       */
      const wiping = emptiedCollections(next, seed);
      const body = draftToUpdate(next, seed, {
        confirmEmptyCollections: wiping.length > 0,
      });
      const saved = await updateTitleCall(assetId, body);
      setSeed(saved);
      await refresh(assetId);
    });
  }

  /**
   * Register, mint a scoped write URL, PUT the blocks, verify server-side.
   *
   * The job row exists from the first call, so a browser that dies mid-upload
   * leaves a visible queued job with no source rather than nothing at all —
   * and the dispatcher skips exactly that state without failing it.
   */
  async function uploadMaster(file: File, forAsset?: string) {
    /**
     * ⚠️ `forAsset` exists because `createTitle` calls this in the same tick it
     * calls `setAssetId`, and a state setter has not flushed by then — reading
     * `assetId` here would still be null and the upload would silently not
     * happen. The caller that knows the id passes it.
     */
    const id = forAsset ?? assetId;
    if (!id) return;
    setError(null);

    /**
     * The length, from the file that already knows it.
     *
     * ⚠️ NOT awaited. A demux takes as long as it takes and the upload is what
     * the human is waiting on — blocking one on the other would put a stall in
     * front of the only step that matters, to save somebody typing two digits.
     *
     * ⚠️ Fills a BLANK field only. Container metadata is wrong often enough
     * that a person correcting it must not be corrected back on the next pick,
     * and re-picking the same file to retry an upload is a normal thing to do.
     */
    void measureVideoDuration(file).then((seconds) => {
      if (seconds === null) return;
      setDraft((current) =>
        current.duration.trim()
          ? current
          : { ...current, duration: String(seconds) }
      );
    });

    setUpload({ pct: null, label: "Registering the ingest…" });
    try {
      const existing = job?.id
        ? { id: job.id }
        : (
            await api.startIngest(id, {
              extension: "mp4",
            })
          ).job;
      setUpload({
        pct: null,
        label: "Asking storage for somewhere to put it…",
      });
      const { uploadUrl } = await api.ingestUploadUrl(existing.id);
      await uploadInBlocks(uploadUrl, file, {
        contentType: "video/mp4",
        onProgress: (put, total) =>
          setUpload({
            pct: Math.round((put / total) * 100),
            label: `Uploading ${Math.round(put / 1e6)} of ${Math.round(
              total / 1e6
            )} MB`,
          }),
      });
      // The server reads the blob back. "I finished" from a client that lost
      // its last block would put a truncated file on a serial farm.
      setUpload({ pct: 100, label: "Verifying the upload…" });
      await api.ingestUploaded(existing.id);
      await refresh(id);
    } catch (e) {
      fail(e);
      await refresh(id).catch(() => {});
    } finally {
      setUpload(null);
    }
  }

  async function send() {
    if (!job) return;
    await run("Sending…", async () => {
      const answer = await api.submitIngest(job.id);
      setSent({ dispatch: answer.dispatch });
      setLadder(answer.steps);
      setJob(answer.job);
      onFinished();
    });
  }

  const canLeaveIdentity = Boolean(assetId);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      data-testid="vod-wizard"
    >
      <DialogTitle>New VOD title</DialogTitle>
      <DialogContent dividers>
        {/**
         * ⚠️ Eight rungs is more than a horizontal `Stepper` is comfortable
         * with. `alternativeLabel` puts each word UNDER its mark instead of
         * beside it, which is what buys the room; the gaps then keep the marks
         * apart once the row wraps on a narrow window.
         *
         * Kept horizontal rather than turned into a left rail because the
         * ORDER is the thing being communicated, and a run of numbered marks
         * reads as a sequence at a glance.
         */}
        <Stepper
          nonLinear
          alternativeLabel
          activeStep={index}
          sx={{
            mt: 1,
            mb: 4,
            flexWrap: "wrap",
            rowGap: 2.5,
            columnGap: 1,
            // Line up with the marks rather than the (now lower) labels.
            "& .MuiStepConnector-root": { top: 12 },
          }}
        >
          {RAIL.map((entry, i) => {
            const result =
              entry.key === "review" ? undefined : stateOf(entry.key);
            const done = result?.state === "done";
            const skipped = result?.state === "skipped";
            return (
              <Step key={entry.key} completed={done}>
                <StepButton
                  onClick={() => setIndex(i)}
                  disabled={i > 0 && !canLeaveIdentity}
                  data-testid={`wizard-step-${entry.key}`}
                  data-state={
                    entry.key === "review"
                      ? "review"
                      : (result?.state ?? "todo")
                  }
                  icon={
                    done ? (
                      <CheckCircleIcon
                        color="success"
                        fontSize="small"
                        data-testid={`wizard-done-${entry.key}`}
                      />
                    ) : undefined
                  }
                >
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: skipped ? 0.5 : 1,
                      whiteSpace: "nowrap",
                      px: 0.5,
                    }}
                  >
                    {TITLE_CASE(entry.label)}
                  </Typography>
                </StepButton>
              </Step>
            );
          })}
        </Stepper>

        {agreementNeeded && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            data-testid="wizard-agreement-needed"
          >
            Nothing can be uploaded until {agreementNeeded} has been accepted.{" "}
            <Link to="/supplier/agreements">Open it now</Link> — come back to
            this step afterwards and the file is still yours to choose.
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} data-testid="wizard-error">
            {error}
          </Alert>
        )}
        {busy && <LinearProgress sx={{ mb: 2 }} />}

        {/* 🚨 Above the step body, not inside the master step (`ART-9`).
            A master started from step one — because the operator arrived with
            a file already chosen — would otherwise upload behind a screen that
            shows nothing happening, which reads as a dialog that ignored the
            file. It is also simply better where it is: an upload survives
            stepping away to fill in the description. */}
        {upload && (
          <Box sx={{ mb: 2 }} data-testid="wizard-upload-progress">
            <Typography variant="body2">{upload.label}</Typography>
            <LinearProgress
              variant={upload.pct === null ? "indeterminate" : "determinate"}
              value={upload.pct ?? 0}
            />
          </Box>
        )}

        {step === "identity" && (
          <Stack spacing={2}>
            <SectionHead
              title="Title details"
              subtitle="What the catalogue calls this work. Everything else follows from it."
            />
            <Field
              label="Name"
              value={draft.originalTitle}
              onChange={(e) =>
                setDraft({ ...draft, originalTitle: e.target.value })
              }
              inputProps={{ "data-testid": "wizard-name" }}
              disabled={Boolean(assetId)}
              required
            />
            <Field
              label="Distributor id"
              value={draft.externalId}
              onChange={(e) =>
                setDraft({ ...draft, externalId: e.target.value })
              }
              inputProps={{ "data-testid": "wizard-external-id" }}
              disabled={Boolean(assetId)}
              required
            />
            {/* ---- What kind of thing this is --------------------------
                Frozen after create, like the two fields above it, because
                upstream will not move a row between types and a picker that
                looked like it could would silently keep the old one.

                Hidden entirely while `WIZARD_KINDS` holds one entry: a select
                with a single option is a control that cannot be operated. */}
            {WIZARD_KINDS.length > 1 && (
              <Field
                select
                label="Kind"
                value={draft.type}
                onChange={(e) => {
                  const type = e.target.value as VodAssetType;
                  // Parent links that the new type cannot carry are dropped
                  // rather than sent and refused: `field_not_on_type` about a box
                  // that is no longer on screen is not an answerable error.
                  const allowed = PARENT_FIELDS_BY_TYPE[type];
                  setDraft({
                    ...draft,
                    type,
                    seriesId: allowed.includes("seriesId")
                      ? draft.seriesId
                      : "",
                    seasonId: allowed.includes("seasonId")
                      ? draft.seasonId
                      : "",
                    seasonNumber: allowed.includes("seasonNumber")
                      ? draft.seasonNumber
                      : "",
                    episodeNumber: allowed.includes("episodeNumber")
                      ? draft.episodeNumber
                      : "",
                  });
                }}
                disabled={Boolean(assetId)}
                helperText={
                  isContainerType(kind)
                    ? "A container: artwork, text and a licence window. No video file."
                    : "Something a guest watches, so it carries a master file."
                }
                inputProps={{ "data-testid": "wizard-type" }}
              >
                {WIZARD_KINDS.map((type) => (
                  <MenuItem
                    key={type}
                    value={type}
                    data-testid={`wizard-type-${type}`}
                  >
                    {TITLE_CASE(type)}
                  </MenuItem>
                ))}
              </Field>
            )}

            {PARENT_FIELDS_BY_TYPE[kind].map((field) => {
              const options =
                field === "seriesId"
                  ? parents.series
                  : field === "seasonId"
                    ? parents.seasons
                    : null;
              // A NUMBER — typed, because nothing else knows it.
              if (options === null) {
                return (
                  <Field
                    key={field}
                    label={PARENT_FIELD_LABELS[field]}
                    value={draft[field as "seasonNumber"]}
                    onChange={(e) =>
                      setDraft({ ...draft, [field]: e.target.value })
                    }
                    disabled={Boolean(assetId)}
                    helperText="A whole number from 1. Numbering from 0 puts it before the first one everywhere it is ordered."
                    inputProps={{ "data-testid": `wizard-${field}` }}
                  />
                );
              }
              // A PARENT — picked from the caller's own rows, never typed.
              return (
                <Field
                  key={field}
                  select
                  label={PARENT_FIELD_LABELS[field]}
                  value={draft[field as "seriesId"]}
                  onChange={(e) =>
                    setDraft({ ...draft, [field]: e.target.value })
                  }
                  disabled={Boolean(assetId)}
                  helperText={
                    options.length === 0
                      ? field === "seasonId" && !draft.seriesId
                        ? "Pick the series first."
                        : `You have no ${field === "seriesId" ? "series" : "seasons in that series"} yet — create one first.`
                      : "Picked, never typed: there is no foreign key upstream, so a wrong id files this inside somebody else's series."
                  }
                  inputProps={{ "data-testid": `wizard-${field}` }}
                >
                  {options.map((row) => (
                    <MenuItem key={row.assetId} value={row.assetId}>
                      {row.originalTitle || row.assetId}
                    </MenuItem>
                  ))}
                </Field>
              );
            })}

            {!assetId ? (
              <Box>
                <Button
                  variant="contained"
                  onClick={createTitle}
                  // Only the NAME gates the create now. A nameless row is
                  // still refused upstream-side and still worth refusing here:
                  // a catalogue filling up with blank titles is harder to
                  // notice, and harder to undo, than a disabled button.
                  disabled={!draft.originalTitle.trim() || Boolean(busy)}
                  data-testid="wizard-create"
                >
                  Create title
                </Button>
              </Box>
            ) : (
              <Alert severity="success" data-testid="wizard-created">
                Created as {assetId}. It is in no package yet, so nobody can see
                it.
              </Alert>
            )}

            {/* ---- About the work ----------------------------------------
                None of this gates the submission. It is here because until
                now NOBODY OUTSIDE NENDA COULD FILL IT IN: staff have
                `AssetEditorDialog`, `SupplierTitlePage` offers only the images
                panel, and the wizard offered neither — so a supplier's title
                arrived with no length, no studio and no language, and
                `findings.ts` reported `duration_missing` at a partner who had
                no field to answer it with.

                🚨 `Original language` is the one with a consequence beyond
                metadata: `uploadLanguage` derives the artwork language from it,
                so with no field here every first poster from every supplier was
                filed as `sv` — and the images panel has no per-row language
                control to correct it afterwards. */}
            {assetId && (
              <>
                <SectionHead
                  title="About the work"
                  subtitle="None of this blocks the hand-in. It is what a room shows beside the film, and what a reviewer reads."
                />
                <Field
                  label="Original language"
                  placeholder="sv"
                  helperText="Two or three letters, no region — the column cannot hold “pt-BR”. New artwork is filed under this."
                  value={draft.originalLanguage}
                  onChange={(e) =>
                    setDraft({ ...draft, originalLanguage: e.target.value })
                  }
                  inputProps={{ "data-testid": "wizard-original-language" }}
                />
                <Field
                  label="Country of origin"
                  placeholder="se"
                  value={draft.countryOfOrigin}
                  onChange={(e) =>
                    setDraft({ ...draft, countryOfOrigin: e.target.value })
                  }
                  inputProps={{ "data-testid": "wizard-country" }}
                />
                <Field
                  label="Production year"
                  placeholder="e.g. 2024"
                  helperText="The year a guest sees. Leave it blank rather than guessing."
                  value={draft.productionYear}
                  onChange={(e) =>
                    setDraft({ ...draft, productionYear: e.target.value })
                  }
                  inputProps={{ "data-testid": "wizard-production-year" }}
                />
                {/**
                 * Minutes, because that is the only unit anybody knows a film
                 * in. The catalogue stores SECONDS (`Asset.duration`; a media's
                 * own `durationMillis` is a third unit again), so the
                 * conversion lives here at the field and the draft keeps the
                 * stored unit — one place to be wrong instead of three.
                 *
                 * ⚠️ The displayed value is ROUNDED and the stored one is not
                 * rewritten unless somebody edits this field. A title holding
                 * 5 510 seconds shows 92 and stays 5 510; converting on every
                 * open would restate it as 5 520 and quietly drift the
                 * catalogue one save at a time.
                 *
                 * Optional, and it always was — `draftToUpdate` omits the key
                 * when the text is blank, so an empty field asserts nothing.
                 * Only the label ever suggested otherwise, on a step whose own
                 * subtitle says none of it blocks the hand-in.
                 */}
                <Field
                  label="Length (minutes)"
                  placeholder="e.g. 92"
                  helperText="Optional. Filled in from the video file when you upload one."
                  value={secondsToMinutes(draft.duration)}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      duration: minutesToSeconds(e.target.value),
                    })
                  }
                  inputProps={{ "data-testid": "wizard-duration" }}
                />
                <Field
                  label="Studio"
                  value={draft.studio}
                  onChange={(e) =>
                    setDraft({ ...draft, studio: e.target.value })
                  }
                  inputProps={{ "data-testid": "wizard-studio" }}
                />
                <Field
                  select
                  label="Genres"
                  value={draft.genreIds}
                  SelectProps={{
                    multiple: true,
                    renderValue: (selected) =>
                      (selected as string[])
                        .map(
                          (id) =>
                            genres.find((g) => g.genreId === id)?.name ?? id
                        )
                        .join(", "),
                  }}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      genreIds:
                        typeof e.target.value === "string"
                          ? e.target.value.split(",")
                          : (e.target.value as unknown as string[]),
                    })
                  }
                  helperText="Picked, never typed — an invented genre is a permanent second shelf upstream."
                  inputProps={{ "data-testid": "wizard-genres" }}
                >
                  {genres.map((genre) => (
                    <MenuItem key={genre.genreId} value={genre.genreId}>
                      {genre.name}
                    </MenuItem>
                  ))}
                </Field>
                <StringListField
                  label="Keywords"
                  hint="Two spellings of one word are two rows upstream, and both have to be searched for."
                  value={draft.keywords}
                  placeholder="nordic noir"
                  onChange={(next) => setDraft({ ...draft, keywords: next })}
                  testId="wizard-keyword"
                />
                {/* An explicit Save, not a blur handler. Every other step here
                    persists on a deliberate press, and a field that saved
                    itself while somebody was still typing a year would produce
                    exactly the mid-typing refusals (`202`) the server is right
                    to give. */}
                <Box>
                  <Button
                    variant="outlined"
                    onClick={() => void saveDraft(draft)}
                    disabled={Boolean(busy)}
                    data-testid="wizard-save-details"
                  >
                    Save details
                  </Button>
                </Box>
              </>
            )}
          </Stack>
        )}

        {step === "master" && (
          <Stack spacing={2}>
            <SectionHead
              title="Video file"
              subtitle="The master. It is uploaded straight to storage — the bytes never pass through Foyer."
            />
            <input
              ref={filePick}
              type="file"
              accept="video/mp4"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file) void uploadMaster(file);
              }}
              data-testid="wizard-file-input"
            />
            <Box>
              <Button
                variant="outlined"
                onClick={() => filePick.current?.click()}
                disabled={Boolean(upload)}
                data-testid="wizard-pick-file"
              >
                Select .mp4 file
              </Button>
            </Box>
            {stateOf("master")?.state === "done" && (
              <Alert severity="success" data-testid="wizard-master-done">
                The file is uploaded and verified. Nothing has been sent for
                encoding yet.
              </Alert>
            )}
          </Stack>
        )}

        {step === "subtitles" && (
          <Stack spacing={2}>
            <SectionHead
              title="Subtitles"
              subtitle="Add every language now. They ride alongside the delivery and cannot be added after it."
            />
            {job ? (
              <>
                <SubtitlesTab
                  surface={surface}
                  jobs={[job]}
                  onChanged={async () => {
                    if (assetId) await refresh(assetId);
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={stateOf("subtitles")?.state === "done"}
                      /**
                       * ⚠️ Disabled only once the ingest has actually STARTED,
                       * never merely because it is ticked.
                       *
                       * It used to disable itself the moment it was checked,
                       * which reads as a broken control and traps the mis-tick
                       * that matters: "that is all the subtitles" said by
                       * accident ships a title without the subtitles somebody
                       * was still preparing, and they cannot be added after
                       * the package is registered.
                       */
                      disabled={job.phase !== "queued"}
                      onChange={(e) =>
                        run("Saving…", async () => {
                          await api.ingestSubtitlesDone(
                            job.id,
                            e.target.checked
                          );
                          if (assetId) await refresh(assetId);
                        })
                      }
                      data-testid="wizard-subtitles-done"
                    />
                  }
                  /**
                   * The answer, not the files. "None uploaded yet" and "this
                   * title has none" are identical in storage and opposite in
                   * meaning — and without somebody saying which, the title
                   * waits forever on a file that was never coming.
                   */
                  label="That is all the subtitles for this title (including none at all)"
                />
              </>
            ) : (
              <Alert severity="info">Upload the video file first.</Alert>
            )}
          </Stack>
        )}

        {step === "description" && (
          <Stack spacing={2}>
            <SectionHead
              title="Description"
              subtitle="One language needs a title and all three synopsis lengths — the catalogue has no partial translation."
            />
            <Box>
              <Button
                variant="outlined"
                onClick={() => setPanel("text")}
                data-testid="wizard-open-text"
              >
                Manage descriptions
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {draft.localizedInfo.length} language
              {draft.localizedInfo.length === 1 ? "" : "s"} started.
            </Typography>
          </Stack>
        )}

        {step === "artwork" && (
          <Stack spacing={2}>
            <SectionHead
              title="Artwork"
              subtitle="What a guest sees before they press play."
            />
            {/**
             * ART-2 put a picker here beside a "Manage artwork" button, and the
             * two answered the same question differently: the picker always
             * appended a `poster` in one derived language, while the panel
             * behind the button edited the actual list. An operator who wanted
             * a cover, or a second language, or to replace what they had just
             * uploaded, had to use the picker and then go and correct it.
             *
             * So there is ONE door now. The panel owns uploading, on the row
             * whose square you clicked, in that row's language and type — which
             * is the same upload, aimed by the operator instead of guessed at.
             *
             * ⚠️ The browser measures the image, because the server has no
             * decoder and never will. An unmeasurable file is still uploaded —
             * `0 x 0` is a legal row upstream and 7 220 production rows are
             * exactly that.
             */}
            <Box>
              <Button
                variant="contained"
                size="large"
                disabled={!assetId || Boolean(busy)}
                onClick={() => setPanel("images")}
                data-testid="wizard-open-images"
              >
                Manage artwork
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Pick a language, then click a square to choose a picture. JPEG,
              PNG, WebP or AVIF, up to 10 MB.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {draft.images.length} image
              {draft.images.length === 1 ? "" : "s"}.
            </Typography>
          </Stack>
        )}

        {step === "rights" && (
          <Stack spacing={2}>
            <SectionHead
              title="Licence window"
              subtitle="Where and when this may play."
            />
            {/**
             * ⚠️ Not paperwork. An empty `contentRights` is the PERMISSIVE case:
             * no window means available everywhere, forever. This step is the
             * only moment anybody states what was actually licensed.
             */}
            <Alert severity="warning">
              A title with no window is available everywhere, forever.
            </Alert>
            <AvailabilityEditor
              rows={draft.contentRights}
              onChange={(rows) => setDraft({ ...draft, contentRights: rows })}
            />
            <Box>
              <Button
                variant="outlined"
                onClick={() => saveDraft(draft)}
                disabled={Boolean(busy)}
                data-testid="wizard-save-rights"
              >
                Save
              </Button>
            </Box>
          </Stack>
        )}

        {step === "packages" && (
          <Stack spacing={2}>
            <SectionHead
              title="Packages"
              subtitle="Which packages carry this title. A title in none plays for nobody."
            />
            {/**
             * 🚨 A supplier is TOLD, not asked. `docs/content-packages.md` §5
             * rules that a creator upload enters a package by a pin written at
             * screening, and the owner route answers a body naming packages
             * with 403 `packages_decided_by_screening`. Drawing a disabled
             * picker here would suggest the decision is theirs and merely
             * unavailable.
             */}
            {owner ? (
              <Alert severity="info" data-testid="wizard-packages-screening">
                Nenda decides which packages carry this title, when the material
                is reviewed.
              </Alert>
            ) : packages.length === 0 ? (
              <Alert severity="warning">
                No packages are configured, so there is nowhere for this title
                to go.
              </Alert>
            ) : (
              <Stack>
                {packages.map((p) => (
                  <FormControlLabel
                    key={p.id}
                    control={
                      <Checkbox
                        checked={(job?.entitlements ?? []).includes(p.id)}
                        onChange={(e) => {
                          if (!job) return;
                          const next = e.target.checked
                            ? [...(job.entitlements ?? []), p.id]
                            : (job.entitlements ?? []).filter(
                                (id) => id !== p.id
                              );
                          void run("Saving…", async () => {
                            await api.setIngestEntitlements(job.id, next);
                            if (assetId) await refresh(assetId);
                          });
                        }}
                        data-testid={`wizard-package-${p.id}`}
                      />
                    }
                    label={p.name}
                  />
                ))}
              </Stack>
            )}
            {!owner && !job && (
              <Alert severity="info">Upload the video file first.</Alert>
            )}
          </Stack>
        )}

        {step === "review" && (
          <Stack spacing={2}>
            <SectionHead
              title="Review & send"
              subtitle="Nothing has been encoded or delivered yet."
            />
            <Stack spacing={1}>
              {ladder.map((entry) => (
                <Stack
                  key={entry.step}
                  direction="row"
                  spacing={1}
                  alignItems="flex-start"
                  data-testid={`wizard-review-${entry.step}`}
                  data-state={entry.state}
                >
                  <Typography variant="body2" sx={{ minWidth: 140 }}>
                    {TITLE_CASE(VOD_WIZARD_LABELS[entry.step])}
                  </Typography>
                  <Typography
                    variant="body2"
                    color={
                      entry.state === "done"
                        ? "success.main"
                        : entry.state === "skipped"
                          ? "text.disabled"
                          : "warning.main"
                    }
                  >
                    {entry.state === "done"
                      ? "Ready"
                      : entry.state === "skipped"
                        ? "Decided at screening"
                        : entry.detail}
                  </Typography>
                </Stack>
              ))}
            </Stack>
            {sent && (
              <Alert
                severity={sent.dispatch ? "success" : "info"}
                data-testid="wizard-sent"
              >
                {sent.dispatch
                  ? "Sent. It joins the transcoding queue on the next pass."
                  : "Handed in. Nothing is encoded until Nenda has reviewed the material."}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={() => setIndex((i) => Math.max(0, i - 1))}
          disabled={index === 0}
        >
          Back
        </Button>
        {step === "review" ? (
          <Button
            variant="contained"
            size="large"
            onClick={send}
            disabled={
              !job || missing.length > 0 || Boolean(busy) || Boolean(sent)
            }
            data-testid="wizard-send"
            /**
             * Deliberately louder than the other actions.
             *
             * This is the only irreversible thing in the wizard — everything
             * before it is a draft the supplier can keep editing, and this is
             * the moment the title leaves them and becomes ours to review. A
             * button the same weight as "Back" and "Close" does not say that,
             * and a supplier who cannot find it simply does not deliver.
             */
            sx={{
              px: 4,
              py: 1.25,
              fontSize: 15,
              fontWeight: 700,
              minWidth: 200,
              boxShadow: 2,
            }}
          >
            {/**
             * ⚠️ Promise only what the gate will actually do. An untrusted
             * partner told "Send to transcoding" and then held is a partner
             * who will ask why nothing is happening; the server has already
             * answered this on every ladder read.
             */}
            {owner ? "Hand in" : "Send to transcoding"}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={() => setIndex((i) => Math.min(RAIL.length - 1, i + 1))}
            disabled={!canLeaveIdentity}
            data-testid="wizard-next"
          >
            Next
          </Button>
        )}
      </DialogActions>

      <LocalisedContentPanel
        open={panel === "text"}
        rows={draft.localizedInfo}
        onClose={() => setPanel(null)}
        onSave={(rows) => {
          setPanel(null);
          void saveDraft({ ...draft, localizedInfo: rows });
        }}
      />
      <LocalisedImagesPanel
        open={panel === "images"}
        images={draft.images}
        assetId={assetId ?? ""}
        surface={surface}
        onClose={() => setPanel(null)}
        onSave={(images) => {
          setPanel(null);
          void saveDraft({ ...draft, images });
        }}
        onUploaded={(saved) => {
          /**
           * ⚠️ IMAGES ONLY, not the whole draft.
           *
           * The old picker re-seeded everything with `setDraft(assetToDraft())`
           * because it was a deliberate trip to a step that did nothing else.
           * An upload is now something done mid-edit — one click inside a panel
           * opened from any step — so replacing the draft would throw away a
           * length just measured off the master, a synopsis half typed, or a
           * licence window nobody has saved yet.
           *
           * `seed` still takes the whole row: it is what `draftToUpdate` diffs
           * against, and letting it drift from the server is how a later save
           * restates a collection it never touched.
           */
          setSeed(saved);
          setDraft((current) => ({
            ...current,
            images: assetToDraft(saved).images,
          }));
        }}
      />
    </Dialog>
  );
}
